import { OTP_PROOFKEY, OTP_TOKEN } from '@/lib/consts/auth';
import { getUrlQueryParam } from '@/lib/utils/url';

/**
 * Mobile OTP navigates the browser into the game app, so the access token
 * minted alongside the deeplink cannot survive in memory: the game sends the
 * player back as a cold page load carrying only `?proofKey=<code>`.
 *
 * Login authenticates on `playerCode` (the proofKey) alone — the same payload
 * store-frontend sends. The access token is optional: when present it is used
 * for UTMs and campaign metadata.
 *
 * localStorage rather than sessionStorage because the return trip regularly
 * lands in a fresh tab (or a webview handing off to the system browser), where
 * sessionStorage is already gone.
 */
const PENDING_TOKEN_KEY = 'ac_pending_otp_access_token';

/** OTP codes are short-lived — an abandoned attempt must not be paired hours later. */
const PENDING_TOKEN_TTL_MS = 15 * 60 * 1000;

interface PendingOtpAccessToken {
    token: string;
    expiresAt: number;
}

export interface OtpCredentials {
    proofKey: string;
    token?: string;
}

export const persistPendingOtpAccessToken = (token: string | undefined): void => {
    if (typeof window === 'undefined' || !token) {
        return;
    }
    const pending: PendingOtpAccessToken = {
        token,
        expiresAt: Date.now() + PENDING_TOKEN_TTL_MS,
    };
    try {
        localStorage.setItem(PENDING_TOKEN_KEY, JSON.stringify(pending));
    } catch {
        // private mode / quota — the callback then relies on a `token` query param
    }
};

export const readPendingOtpAccessToken = (): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    let raw: string | null = null;
    try {
        raw = localStorage.getItem(PENDING_TOKEN_KEY);
    } catch {
        return null;
    }
    if (!raw) {
        return null;
    }
    const pending = parsePendingOtpAccessToken(raw);
    if (!pending || pending.expiresAt <= Date.now()) {
        clearPendingOtpAccessToken();
        return null;
    }
    return pending.token;
};

const parsePendingOtpAccessToken = (raw: string): PendingOtpAccessToken | null => {
    try {
        const parsed = JSON.parse(raw) as Partial<PendingOtpAccessToken>;
        if (typeof parsed?.token !== 'string' || typeof parsed?.expiresAt !== 'number') {
            return null;
        }
        return { token: parsed.token, expiresAt: parsed.expiresAt };
    } catch {
        return null;
    }
};

export const clearPendingOtpAccessToken = (): void => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        localStorage.removeItem(PENDING_TOKEN_KEY);
    } catch {
        // nothing to do — a stale entry expires on its own
    }
};

/**
 * A `token` query param wins over storage: a publisher that redirects back with
 * both halves is authoritative over whatever this device stashed earlier.
 * A proofKey with no token is still a valid callback — login proceeds with
 * playerCode only, matching store-frontend.
 */
export const resolveOtpCredentials = (): OtpCredentials | null => {
    const proofKey = getUrlQueryParam(OTP_PROOFKEY);
    if (!proofKey) {
        return null;
    }
    const token = getUrlQueryParam(OTP_TOKEN) ?? readPendingOtpAccessToken() ?? undefined;
    return token ? { proofKey, token } : { proofKey };
};
